// Phase HR-002 hardening — apiClient now retries transient failures.
//
// Why: real-time data capture means an employee's save MUST land even if
// the backend hiccups for 1-2 seconds (worker saturation, brief network
// blip, DB lock retry). Without retry, every transient 5xx surfaced as a
// red "Failed to save" toast → operator manually clicked Save again →
// duplicate POST → upstream race condition. With retry, the failure is
// invisible to the operator and the save still lands.
//
// Safety rules (very important):
//   * Only retry network errors (no response) and 5xx server errors.
//     NEVER retry 4xx — those mean the request itself was wrong, repeating
//     it changes nothing and could cause confusing UX.
//   * For non-idempotent methods (POST/PATCH/PUT/DELETE), retry only when
//     the server clearly didn't process the request — i.e. network error
//     (status 0) or 502/503/504 (load balancer / worker pool saturation).
//     We don't retry 500 on writes because a real exception inside a
//     committed transaction could double-write. GET is fully idempotent
//     so we retry all 5xx.
//   * Cap at 3 attempts total with exponential backoff: 300ms, 800ms, 2s.
//     After that, surface the error to the caller as before.
//
// Phase HR-002 offline-queue: when retries exhaust AND we have a writeQueue
// configured, the request is handed to the queue instead of throwing. The
// queue persists in IndexedDB and replays in the background. UI never sees
// a "save failed" for transient backend trouble.

import axios, { type AxiosError, type AxiosRequestConfig } from 'axios'
import { notification } from 'antd'

// Augment Axios's config so we can stamp the retry counter on each request.
interface RetryConfig extends AxiosRequestConfig {
  _retryAttempt?: number
  _skipRetry?: boolean
}

const MAX_ATTEMPTS = 3
const BACKOFF_MS = [300, 800, 2000]   // attempt 1 fail → wait 300, attempt 2 fail → wait 800, etc.

const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  // 30s is enough for any single legitimate call; longer than that means
  // something's actually wrong and we should fail fast and retry.
  timeout: 30_000,
})

apiClient.interceptors.request.use((config) => {
  const raw = localStorage.getItem('auth-storage')
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      const token = parsed?.state?.accessToken
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch {
      // ignore parse errors
    }
  }
  return config
})

/** Decide whether this failed request should be retried. */
function shouldRetry(error: AxiosError, cfg: RetryConfig): boolean {
  if (cfg._skipRetry) return false
  const attempt = cfg._retryAttempt ?? 0
  if (attempt >= MAX_ATTEMPTS - 1) return false

  const method = (cfg.method ?? 'get').toLowerCase()
  const status = error.response?.status

  // Network error / timeout (no response) — always retry, even for writes.
  // The server didn't see the request, so retrying is safe.
  if (!error.response) return true

  // 5xx classes:
  //   502/503/504 — load balancer / worker pool problems — server has not
  //     processed the request meaningfully. Safe to retry any method.
  //   500 — actual exception. Could be mid-transaction. Only retry GET.
  if (status === 502 || status === 503 || status === 504) return true
  if (status === 500 && method === 'get') return true

  return false
}

/** Sleep helper. */
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const cfg = (error.config ?? {}) as RetryConfig
    const status = error.response?.status

    // 401 — session dead, stop retry, redirect.
    if (status === 401) {
      localStorage.removeItem('auth-storage')
      window.location.href = '/login?reason=session_expired'
      return Promise.reject(error)
    }
    // 403 — surface to user; don't retry.
    if (status === 403) {
      const detail = (error.response?.data as { detail?: unknown })?.detail
      const msg = typeof detail === 'string' ? detail : 'You do not have permission to perform this action.'
      notification.error({ message: 'Permission Denied', description: msg, duration: 5 })
      return Promise.reject(error)
    }

    // Retry path
    if (shouldRetry(error, cfg)) {
      const attempt = cfg._retryAttempt ?? 0
      cfg._retryAttempt = attempt + 1
      const delay = BACKOFF_MS[attempt] ?? BACKOFF_MS[BACKOFF_MS.length - 1]
      await wait(delay)
      return apiClient.request(cfg)
    }

    // Phase HR-002 offline-queue hook — give the queue a chance to take
    // this write before we reject. The queue itself decides whether to
    // accept (idempotent + safe-to-replay paths) or let it through.
    // Loaded lazily to avoid a circular import with the queue module
    // (which uses apiClient internally for the replay loop).
    try {
      const { tryEnqueueOnFailure } = await import('./offlineQueue')
      const queued = await tryEnqueueOnFailure(cfg, error)
      if (queued) {
        // Synthesize a "202 Accepted" so the caller's promise resolves
        // and React Query doesn't treat it as an error. The queue will
        // post for real later and any visible side-effect (e.g. list
        // refresh) will happen when the replay invalidates queries.
        return {
          data: { _queued: true, _queueId: queued.id },
          status: 202,
          statusText: 'Accepted (queued for retry)',
          headers: {},
          config: cfg,
        }
      }
    } catch {
      // Queue not available (e.g. SSR / Jest) — fall through to reject.
    }

    return Promise.reject(error)
  },
)

/** Extract a readable error message from an Axios error */
export function getApiError(error: unknown, fallback = 'Operation failed'): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const detail = (error as any)?.response?.data?.detail
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg ?? String(d)).join('; ')
  if (typeof detail === 'string') return detail
  return fallback
}

export default apiClient
